import type {
  AsymmetricCoreCrypto,
  SenderPrivateKey,
  SenderPublicKey,
} from 'metaserver/static/js/encrypted_folder/crypto/asymmetric_core_crypto';
import {openAuth, sealAuth} from 'metaserver/static/js/encrypted_folder/crypto/hpke/hpkeSuite';
import {WebcryptoHPKESuite} from 'metaserver/static/js/encrypted_folder/crypto/hpke/webcryptoHpkeSuite';
import {
  AlgorithmNotFoundError,
  KeyMismatchError,
  ProhibitedUseError,
} from 'metaserver/static/js/encrypted_folder/crypto/errors';
import type {
  Algorithm,
  EncryptedFileKeyDraft,
  EncryptedKeyPairDraft,
  SymmetricKey,
  KeyPairDraft,
  PublicKey,
  PublicKeyDraft,
} from 'metaserver/static/js/encrypted_folder/crypto/types';
import {KeyEncoding} from 'metaserver/static/js/encrypted_folder/crypto/hpke/keyEncoding';
import {concat} from 'metaserver/static/js/encrypted_folder/crypto/hpke/utils';
import {bytesToHexString} from 'metaserver/static/js/encrypted_folder/utils/bytes';

export class SenderAuthP256AsymmetricCoreCrypto implements AsymmetricCoreCrypto {
  static algorithm: Algorithm = 'SENDER_AUTH_HPKE_DHKEM_P256_HKDF_SHA256_HKDF_SHA256_AES256_GCM';

  senderAuthenticated(): boolean {
    return true;
  }

  async generateKeyPair(): Promise<KeyPairDraft> {
    const {publicKey, privateKey} = await WebcryptoHPKESuite.generateKeyPair();
    const privateKeyData = await KeyEncoding.toPKCS8(privateKey);
    const publicKeyData = await KeyEncoding.toDer(publicKey);

    return {
      algorithm: SenderAuthP256AsymmetricCoreCrypto.algorithm,
      privateKeyData: privateKeyData,
      publicKey: {
        keyData: publicKeyData,
        algorithm: SenderAuthP256AsymmetricCoreCrypto.algorithm,
      },
    };
  }

  async encryptKeyPair(
    plainText: KeyPairDraft,
    receiverKey: PublicKeyDraft,
    senderKey: SenderPrivateKey
  ): Promise<EncryptedKeyPairDraft> {
    if (!senderKey) {
      // TODO(EEF-939): Refactor interfaces to prevent "null" for required parameters
      throw new ProhibitedUseError();
    }

    if (
      receiverKey.algorithm !== SenderAuthP256AsymmetricCoreCrypto.algorithm ||
      senderKey.algorithm !== SenderAuthP256AsymmetricCoreCrypto.algorithm
    ) {
      throw new AlgorithmNotFoundError();
    }

    const aad = new TextEncoder().encode(
      SenderAuthP256AsymmetricCoreCrypto.algorithm.toLocaleLowerCase()
    );

    const deserializedPublicReceiverKey = await KeyEncoding.fromDer(receiverKey.keyData);
    const deserializedPrivateSenderKey = await KeyEncoding.fromPKCS8(senderKey.privateKeyData);
    const {encappedKey, cipherText} = await sealAuth(
      WebcryptoHPKESuite,
      deserializedPublicReceiverKey,
      deserializedPrivateSenderKey,
      aad,
      plainText.privateKeyData
    );
    const encryptedPrivateKey = concat([encappedKey, cipherText]);

    return {
      algorithm: plainText.algorithm,
      encryptedPrivateKeyData: encryptedPrivateKey,
      publicKeyData: plainText.publicKey.keyData,
    };
  }

  async decryptKeyPair(
    cipherText: EncryptedKeyPairDraft,
    receiverKey: KeyPairDraft,
    senderKey: SenderPublicKey
  ): Promise<KeyPairDraft> {
    if (!senderKey) {
      // TODO(EEF-939): Refactor interfaces to prevent "null" for required parameters
      throw new ProhibitedUseError();
    }

    if (
      receiverKey.publicKey.algorithm !== SenderAuthP256AsymmetricCoreCrypto.algorithm ||
      senderKey.algorithm !== SenderAuthP256AsymmetricCoreCrypto.algorithm
    ) {
      throw new AlgorithmNotFoundError();
    }

    const aad = new TextEncoder().encode(
      SenderAuthP256AsymmetricCoreCrypto.algorithm.toLocaleLowerCase()
    );

    const encodedPrivateKeyData = cipherText.encryptedPrivateKeyData;
    const enc = encodedPrivateKeyData.slice(0, WebcryptoHPKESuite.info().nEnc);
    const cipherTextData = encodedPrivateKeyData.slice(WebcryptoHPKESuite.info().nEnc);
    const deserializedPrivateReceiverKey = await KeyEncoding.fromPKCS8(receiverKey.privateKeyData);
    const deserializedPublicSenderKey = await KeyEncoding.fromDer(senderKey.keyData);
    const decryptedData = await openAuth(
      WebcryptoHPKESuite,
      enc,
      deserializedPrivateReceiverKey,
      deserializedPublicSenderKey,
      aad,
      cipherTextData
    );

    return {
      algorithm: cipherText.algorithm,
      privateKeyData: decryptedData,
      publicKey: {
        keyData: cipherText.publicKeyData,
        algorithm: cipherText.algorithm,
      },
    };
  }

  async encryptKey(
    plainText: SymmetricKey,
    receiverKey: PublicKey,
    senderKey: SenderPrivateKey
  ): Promise<EncryptedFileKeyDraft> {
    if (!senderKey) {
      // TODO(EEF-939): Refactor interfaces to prevent "null" for required parameters
      throw new ProhibitedUseError();
    }

    if (
      receiverKey.algorithm !== SenderAuthP256AsymmetricCoreCrypto.algorithm ||
      senderKey.algorithm !== SenderAuthP256AsymmetricCoreCrypto.algorithm
    ) {
      throw new AlgorithmNotFoundError();
    }

    const aad = new TextEncoder().encode(
      SenderAuthP256AsymmetricCoreCrypto.algorithm.toLocaleLowerCase()
    );

    const deserializedPublicReceiverKey = await KeyEncoding.fromDer(receiverKey.keyData);
    const deserializedPrivateSenderKey = await KeyEncoding.fromPKCS8(senderKey.privateKeyData);
    const {encappedKey, cipherText} = await sealAuth(
      WebcryptoHPKESuite,
      deserializedPublicReceiverKey,
      deserializedPrivateSenderKey,
      aad,
      plainText.keyData
    );
    const encryptedKey = concat([encappedKey, cipherText]);

    return {
      algorithm: plainText.algorithm,
      encryptedKeyData: encryptedKey,
      encryptingKeyId: receiverKey.keyId,
    };
  }

  async decryptKey(
    cipherText: EncryptedFileKeyDraft,
    receiverKey: KeyPairDraft,
    senderKey: SenderPublicKey
  ): Promise<SymmetricKey> {
    if (!senderKey) {
      // TODO(EEF-939): Refactor interfaces to prevent "null" for required parameters
      throw new ProhibitedUseError();
    }

    if (
      receiverKey.publicKey.algorithm !== SenderAuthP256AsymmetricCoreCrypto.algorithm ||
      senderKey.algorithm !== SenderAuthP256AsymmetricCoreCrypto.algorithm
    ) {
      throw new AlgorithmNotFoundError();
    }

    const aad = new TextEncoder().encode(
      SenderAuthP256AsymmetricCoreCrypto.algorithm.toLocaleLowerCase()
    );

    const encodedKeyData = cipherText.encryptedKeyData;
    const enc = encodedKeyData.slice(0, WebcryptoHPKESuite.info().nEnc);
    const cipherTextData = encodedKeyData.slice(WebcryptoHPKESuite.info().nEnc);
    const deserializedPrivateReceiverKey = await KeyEncoding.fromPKCS8(receiverKey.privateKeyData);
    const deserializedPublicSenderKey = await KeyEncoding.fromDer(senderKey.keyData);
    const decryptedData = await openAuth(
      WebcryptoHPKESuite,
      enc,
      deserializedPrivateReceiverKey,
      deserializedPublicSenderKey,
      aad,
      cipherTextData
    );

    return {
      algorithm: cipherText.algorithm,
      keyData: decryptedData,
    };
  }

  async validateKeyPair(keyPair: KeyPairDraft): Promise<void> {
    const decodedPrivateKey = await KeyEncoding.fromPKCS8(keyPair.privateKeyData);
    const extractedPublicKey = await WebcryptoHPKESuite.pkFromSk(decodedPrivateKey);
    const encodedExtractedPublicKey = await KeyEncoding.toDer(extractedPublicKey);

    // Re-encode the provided public key to DER format to prevent minor encoding differences to cause a fail
    const providedPublicKey = await KeyEncoding.fromDer(keyPair.publicKey.keyData);
    const encodedProvidedPublicKey = await KeyEncoding.toDer(providedPublicKey);

    if (
      bytesToHexString(encodedExtractedPublicKey) !== bytesToHexString(encodedProvidedPublicKey) ||
      keyPair.algorithm !== SenderAuthP256AsymmetricCoreCrypto.algorithm ||
      keyPair.publicKey.algorithm !== SenderAuthP256AsymmetricCoreCrypto.algorithm
    ) {
      throw new KeyMismatchError();
    }
  }
}
